import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { EnrichmentEntry, EnrichmentEntryType } from '../enrichmentData';
import { useP_TriggerPersonKnowledgeMutation } from '../graphql/mutations/generated/p_TriggerPersonKnowledge.productivity';
import { useP_PersonKnowledgeListQuery } from '../graphql/queries/generated/p_PersonKnowledgeList.productivity';
import { useP_PollPersonKnowledgeQuery } from '../graphql/queries/generated/p_PollPersonKnowledge.productivity';

const POLL_INTERVAL_MS = 3000;

const ENRICHMENT_ENTRY_TYPES: EnrichmentEntryType[] = [
  'IDENTITY',
  'JOB_TITLE',
  'EMPLOYMENT',
  'INDUSTRY',
  'LOCATION',
  'EDUCATION',
  'EXPERIENCE',
  'SKILL',
  'EXPERTISE',
  'ACHIEVEMENT',
  'MINDSET',
  'GOAL',
  'AFFILIATION',
  'INTEREST',
];

const isEnrichmentEntryType = (value?: string | null): value is EnrichmentEntryType =>
  Boolean(value) && ENRICHMENT_ENTRY_TYPES.includes(value as EnrichmentEntryType);

export type PersonKnowledgeDiscoveryStatus = 'idle' | 'running' | 'completed' | 'failed';

/**
 * Orchestrates the real `triggerPersonKnowledge` / `pollPersonKnowledge` cascade (see
 * okr-knowledge-graphql-plugin in workeffy): fires discovery once, polls Redis-backed job
 * status every few seconds while it's running, and fetches the persisted `PersonKnowledge`
 * entries via generic CRUD once the job reports `COMPLETED`.
 */
const composePersonKnowledgeOwnerId = (workspaceId: string, personId: string) =>
  `${workspaceId}::Person::${personId}`;

const usePersonKnowledgeDiscovery = (workspaceId?: string) => {
  const [, triggerPersonKnowledgeMutation] = useP_TriggerPersonKnowledgeMutation();
  const [activePersonId, setActivePersonId] = useState<string>();
  const [status, setStatus] = useState<PersonKnowledgeDiscoveryStatus>('idle');
  const pollVariables = useMemo(() => ({ personId: activePersonId ?? '' }), [activePersonId]);
  const listVariables = useMemo(
    () => ({
      filter: {
        type: 'EQ',
        field: 'c.personId',
        value:
          activePersonId && workspaceId
            ? composePersonKnowledgeOwnerId(workspaceId, activePersonId)
            : '',
      },
    }),
    [activePersonId, workspaceId],
  );
  const [pollResult, refetchPoll] = useP_PollPersonKnowledgeQuery({
    variables: pollVariables,
    pause: true,
    requestPolicy: 'network-only',
  });
  const [listResult, refetchList] = useP_PersonKnowledgeListQuery({
    variables: listVariables,
    pause: !activePersonId || !workspaceId || status !== 'completed',
    requestPolicy: 'network-only',
  });

  const triggeredPersonIdRef = useRef<string>();
  const triggerPersonKnowledgeMutationRef = useRef(triggerPersonKnowledgeMutation);
  const refetchPollRef = useRef(refetchPoll);
  const refetchListRef = useRef(refetchList);

  useEffect(() => {
    triggerPersonKnowledgeMutationRef.current = triggerPersonKnowledgeMutation;
  }, [triggerPersonKnowledgeMutation]);

  useEffect(() => {
    refetchPollRef.current = refetchPoll;
  }, [refetchPoll]);

  useEffect(() => {
    refetchListRef.current = refetchList;
  }, [refetchList]);

  const trigger = useCallback((personId?: string, email?: string) => {
    if (!personId || !email || triggeredPersonIdRef.current === personId) return;
    triggeredPersonIdRef.current = personId;
    setActivePersonId(personId);
    void triggerPersonKnowledgeMutationRef.current({ personId, email });
  }, []);

  const startPolling = useCallback((personId?: string) => {
    if (!personId) return;
    setActivePersonId(personId);
    setStatus('running');
    refetchPollRef.current({ requestPolicy: 'network-only' });
  }, []);

  useEffect(() => {
    if (status !== 'running' || !activePersonId) return undefined;

    const intervalId = window.setInterval(() => {
      refetchPollRef.current({ requestPolicy: 'network-only' });
    }, POLL_INTERVAL_MS);

    return () => window.clearInterval(intervalId);
  }, [activePersonId, status]);

  useEffect(() => {
    const jobStatus = pollResult.data?.pollPersonKnowledge?.status;
    if (!jobStatus || status !== 'running') return;

    if (jobStatus === 'COMPLETED') {
      setStatus('completed');
      refetchListRef.current({ requestPolicy: 'network-only' });
    } else if (jobStatus === 'FAILED') {
      setStatus('failed');
    }
  }, [pollResult.data, status]);

  const entries: EnrichmentEntry[] = (listResult.data?.PersonKnowledgeList ?? [])
    .map((entry) => {
      if (!entry || !isEnrichmentEntryType(entry.type)) return null;

      return {
        type: entry.type,
        displayName: entry.displayName ?? '',
        description: entry.description ?? '',
        source: entry.source ?? '',
        sourceRef: entry.sourceRef ?? '',
        confidence: entry.confidence ?? 0,
      };
    })
    .filter((entry): entry is EnrichmentEntry => entry !== null);

  return {
    entries,
    hasFetchedEntries: Boolean(listResult.data?.PersonKnowledgeList),
    startPolling,
    status,
    trigger,
  };
};

export default usePersonKnowledgeDiscovery;
